import type { StatusTone } from '@afrilink/ui';

/**
 * `customer_link.dunning_policy` — how it reads on screen.
 *
 * ONE definition, because three surfaces show it (the Hosting accounts list,
 * the customer Hosting tab, the Termination queue) and a flag this consequential
 * must not mean three slightly different things depending on which page you are
 * standing on. That is the same failure as the three payment-gateway label maps.
 *
 * WHAT THE COLUMN ACTUALLY DOES
 * `billing-cron` STEP 3 skips `no_suspend`; STEP 4 skips `no_suspend` AND
 * `no_cancel`. The database enforces it independently via the
 * `provisioning_job_removeacct_guard` BEFORE INSERT trigger, so it holds for
 * every producer, not just the cron (CLAUDE.md §4.12).
 *
 * WHY THE TONES ARE THE WAY ROUND
 * `standard` is the RISKY value, so it is the one that draws the eye — it is
 * the only setting under which an account can be automatically destroyed. The
 * protective values are quiet. Every list in this product colours "normal"
 * green and "unusual" amber; here that convention would be exactly backwards,
 * because the unusual setting is the safe one.
 */

export type DunningPolicy = 'standard' | 'no_cancel' | 'no_suspend';

export interface DunningPolicyMeta {
  label: string;
  tone: StatusTone;
  /** One line, plain, for a tooltip or a helper row. */
  detail: string;
}

export const DUNNING_POLICY: Record<DunningPolicy, DunningPolicyMeta> = {
  standard: {
    label: 'Standard',
    tone: 'overdue',
    detail:
      'Full dunning: this account can be suspended AND automatically terminated after the 60-day horizon.',
  },
  no_cancel: {
    label: 'No auto-cancel',
    tone: 'info',
    detail:
      'Can be suspended for non-payment, but never automatically terminated. Termination requires flipping this to Standard first.',
  },
  no_suspend: {
    label: 'No auto-suspend',
    tone: 'active',
    detail:
      'Exempt from both automatic suspension and automatic termination.',
  },
};

/** Unknown values render as themselves rather than vanishing — a policy this
 *  page cannot name is something to look at, not something to hide. */
export function dunningPolicyMeta(value: string | null | undefined): DunningPolicyMeta {
  if (!value) {
    return {
      label: 'Not set',
      tone: 'draft',
      detail: 'No dunning policy recorded — the database default (Standard) applies.',
    };
  }
  return (
    DUNNING_POLICY[value as DunningPolicy] ?? {
      label: value,
      tone: 'draft',
      detail: 'Unrecognised dunning policy — check customer_link.dunning_policy.',
    }
  );
}

/** True when the account is exposed to automated termination. */
export function canBeAutoTerminated(value: string | null | undefined): boolean {
  return value === 'standard' || !value;
}
